import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  Trash2,
  ChevronLeft,
  Loader2,
  Users,
  Anchor,
  Calendar,
  Settings,
  Edit2,
  Cylinder as CylinderIcon,
  Link2,
  FileSpreadsheet,
  CheckSquare,
  Square,
  Mail,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { plannerApi } from './plannerApi';
import PwaInstallBanner from '../../components/PwaInstallBanner';

const CERT_LEVELS = ['DSD', 'Student', 'OW', 'AOW', 'Rescue', 'Divemaster', 'Instructor'];

const TripDetail = () => {
  const { tripId } = useParams();
  const { token } = useAuth();
  const api = plannerApi(token);
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('days');

  const reload = useCallback(async () => {
    try {
      const t = await api.getTrip(tripId);
      setTrip(t);
    } catch (e) {
      toast.error('Failed to load trip');
    }
  }, [tripId]); // eslint-disable-line

  useEffect(() => {
    let active = true;
    (async () => {
      await reload();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [reload]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }
  if (!trip) return null;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-6 pb-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto">
          <Link to="/planner" className="text-sm opacity-80 hover:opacity-100 flex items-center gap-1 mb-2"><ChevronLeft className="w-4 h-4" /> Back to trips</Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-testid="trip-name">{trip.name}</h1>
          <p className="text-xs opacity-80">{trip.start_date} → {trip.end_date}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 mt-3">
        {/* Tab bar */}
        <div className="flex overflow-x-auto bg-white border rounded-xl p-1 mb-4 gap-1 text-sm">
          {[
            { id: 'days', label: 'Days', icon: Calendar },
            { id: 'divers', label: 'Divers', icon: Users },
            { id: 'crew', label: 'Crew', icon: Anchor },
            { id: 'cylinders', label: 'Cylinders', icon: CylinderIcon },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 min-w-fit flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition ${tab === t.id ? 'bg-[#0A2342] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>

        {tab === 'days' && <DaysTab trip={trip} api={api} reload={reload} />}
        {tab === 'divers' && <DiversTab trip={trip} api={api} reload={reload} />}
        {tab === 'crew' && <CrewTab trip={trip} api={api} reload={reload} />}
        {tab === 'cylinders' && <CylindersTab trip={trip} api={api} reload={reload} />}
        {tab === 'settings' && <SettingsTab trip={trip} api={api} reload={reload} navigate={navigate} />}
      </div>
      <PwaInstallBanner />
    </div>
  );
};

// ===================== DAYS =====================

const _boatLetter = (i) => String.fromCharCode(65 + i);

const DaysTab = ({ trip, api, reload }) => {
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);
  // Each boat has its own launches_count and capacity. Default: 1 boat, 2 launches × 10.
  const [form, setForm] = useState({
    date: trip.start_date,
    boats: [{ name: 'Boat A', launches_count: 2, capacity_per_launch: 10 }],
    launch_times: ['', ''],
  });
  const [busy, setBusy] = useState(false);

  const totalLaunches = form.boats.reduce((s, b) => s + Number(b.launches_count || 0), 0);

  // Resize launch_times whenever total launches changes.
  useEffect(() => {
    setForm((p) => {
      const lt = [...(p.launch_times || [])];
      while (lt.length < totalLaunches) lt.push('');
      lt.length = totalLaunches;
      return { ...p, launch_times: lt };
    });
    // eslint-disable-next-line
  }, [totalLaunches]);

  const updateBoat = (i, patch) => {
    setForm((p) => {
      const boats = p.boats.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
      return { ...p, boats };
    });
  };

  const addBoat = () => {
    setForm((p) => ({
      ...p,
      boats: [...p.boats, { name: `Boat ${_boatLetter(p.boats.length)}`, launches_count: 2, capacity_per_launch: 10 }],
    }));
  };

  const removeBoat = (i) => {
    setForm((p) => {
      if (p.boats.length <= 1) return p;
      return { ...p, boats: p.boats.filter((_, idx) => idx !== i) };
    });
  };

  const subKitUp = (t) => {
    if (!t || !/^\d{2}:\d{2}$/.test(t)) return '';
    const [h, m] = t.split(':').map(Number);
    const total = h * 60 + m - 60;
    if (total < 0) return '';
    const hh = String(Math.floor(total / 60)).padStart(2, '0');
    const mm = String(total % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const createPlan = async (autoGenerate = true) => {
    setBusy(true);
    try {
      await api.generatePlan(trip.id, {
        date: form.date,
        boats: form.boats.map((b) => ({
          name: b.name || '',
          launches_count: Number(b.launches_count),
          capacity_per_launch: Number(b.capacity_per_launch),
        })),
        auto_generate: autoGenerate,
        launch_times: form.launch_times,
      });
      toast.success(autoGenerate ? 'Plan generated' : 'Empty day created — drag divers in yourself');
      await reload();
      setShowNew(false);
      navigate(`/planner/trips/${trip.id}/day/${form.date}`);
    } catch (e) {
      toast.error('Failed to create plan');
    } finally {
      setBusy(false);
    }
  };

  const removePlan = async (date) => {
    if (!window.confirm('Delete plan for ' + date + '?')) return;
    await api.deletePlan(trip.id, date);
    await reload();
  };

  const plans = [...(trip.daily_plans || [])].sort((a, b) => a.date.localeCompare(b.date));

  // Compute max divers across all boats (rough estimate assuming 1 crew per launch).
  const maxDivers = form.boats.reduce((s, b) => {
    const lc = Number(b.launches_count) || 0;
    const cap = Number(b.capacity_per_launch) || 0;
    return s + Math.max(0, Math.floor((lc * (cap - 1)) / 2));
  }, 0);

  return (
    <div className="space-y-3">
      <Button onClick={() => setShowNew(true)} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="new-day-btn">
        <Plus className="w-4 h-4 mr-2" /> Plan a Day
      </Button>

      {plans.length === 0 && (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-500">
          No days planned yet. Choose a date and configure your boats.
        </div>
      )}

      {plans.map((p) => {
        const boats = p.boats || [{ name: 'Boat A', launches_count: p.launches_count, capacity_per_launch: p.capacity_per_launch }];
        return (
          <div key={p.date} className="bg-white rounded-xl border p-4 flex items-center justify-between gap-3" data-testid={`day-card-${p.date}`}>
            <div className="min-w-0">
              <div className="font-semibold text-[#0A2342]">{p.date}</div>
              <div className="text-xs text-gray-500">
                {boats.map((b, i) => `${b.name || `Boat ${_boatLetter(i)}`} (${b.launches_count}×${b.capacity_per_launch})`).join(' · ')} · {p.active_divers_count}/{p.max_divers} divers placed
              </div>
              {p.launch_times && p.launch_times.some((t) => t) && (
                <div className="text-xs text-[#0A2342] mt-0.5">⏱ {p.launch_times.map((t, i) => t ? `L${i+1} ${t}` : null).filter(Boolean).join(' · ')}</div>
              )}
              {p.warnings?.length > 0 && (
                <div className="text-xs text-amber-700 mt-1">⚠ {p.warnings.length} warning{p.warnings.length === 1 ? '' : 's'}</div>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate(`/planner/trips/${trip.id}/day/${p.date}`)} data-testid={`open-day-${p.date}`}>Open</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removePlan(p.date)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        );
      })}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Plan a Day</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date</label>
              <Input type="date" min={trip.start_date} max={trip.end_date} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="plan-day-date" />
            </div>

            {/* Boats */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-gray-500">Boats running this day</label>
                <Button size="sm" variant="outline" onClick={addBoat} data-testid="add-boat-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add boat
                </Button>
              </div>
              {form.boats.map((b, i) => (
                <div key={`boat-${i}`} className="border rounded-xl p-3 bg-slate-50 space-y-2" data-testid={`boat-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input
                      value={b.name || ''}
                      placeholder={`Boat ${_boatLetter(i)}`}
                      onChange={(e) => updateBoat(i, { name: e.target.value })}
                      className="flex-1"
                      data-testid={`boat-name-${i}`}
                    />
                    {form.boats.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeBoat(i)} data-testid={`boat-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-1">Launches</label>
                      <Select value={String(b.launches_count)} onValueChange={(v) => updateBoat(i, { launches_count: Number(v) })}>
                        <SelectTrigger data-testid={`boat-launches-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n} launch{n === 1 ? '' : 'es'}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-1">Total seats per launch</label>
                      <Select value={String(b.capacity_per_launch)} onValueChange={(v) => updateBoat(i, { capacity_per_launch: Number(v) })}>
                        <SelectTrigger data-testid={`boat-capacity-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[8, 10, 12, 13, 14, 16, 18, 20].map((n) => <SelectItem key={n} value={String(n)}>{n} seats</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Launch times — flat across all boats in boat order */}
            <div className="space-y-2">
              <label className="text-xs text-gray-500 block">Launch times (kit-up is 1 hour before)</label>
              {(() => {
                let li = 0;
                return form.boats.map((b, bi) => (
                  <div key={`bt-${bi}`} className="bg-white border rounded-lg p-2 space-y-1">
                    <div className="text-[11px] text-gray-500 font-semibold">{b.name || `Boat ${_boatLetter(bi)}`}</div>
                    {Array.from({ length: Number(b.launches_count) }, (_, j) => {
                      const idx = li++;
                      const t = form.launch_times[idx] || '';
                      return (
                        <div key={`bt-${bi}-${j}`} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-20">Launch {j + 1}</span>
                          <Input
                            type="time"
                            value={t}
                            onChange={(e) => {
                              const next = [...form.launch_times];
                              next[idx] = e.target.value;
                              setForm({ ...form, launch_times: next });
                            }}
                            className="flex-1"
                            data-testid={`plan-day-launch-time-${idx}`}
                          />
                          <span className="text-xs text-gray-500 w-28">Kit-up: <strong>{subKitUp(t) || '—'}</strong></span>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>

            <p className="text-xs text-gray-500">
              Boats run concurrently. Buddy groups will be kept on the same boat.
              With 1 crew per launch, max divers across all boats ≈ <strong>{maxDivers}</strong>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => createPlan(false)} disabled={busy} data-testid="plan-day-empty-btn" title="Create the day with no divers placed — you'll drag them in by hand">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create empty
            </Button>
            <Button onClick={() => createPlan(true)} disabled={busy} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="plan-day-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Auto-generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== DIVERS =====================

const DiversTab = ({ trip, api, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [buddyOpen, setBuddyOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [csvUrl, setCsvUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { proposed: [...], headers }
  const [importChecked, setImportChecked] = useState({}); // id → bool

  const blank = { name: '', certification_level: '', nitrox: false, buddy_name: '', medical_notes: '', notes: '', skip_dive: false, email: '', email_opt_in: true, prior_dives: '', selected_destination: '' };
  const [form, setForm] = useState(blank);

  const sendStaffToCrew = async () => {
    if (!importPreview) return;
    const staffRows = (importPreview.proposed || [])
      .filter((d) => d.is_staff_like && !d.matches_crew)
      .map((d) => ({
        name: d.name,
        // Default to instructor when cert reads Instructor, otherwise divemaster.
        role: (d.certification_level || '').toLowerCase() === 'instructor' ? 'instructor' : 'divemaster',
      }));
    if (staffRows.length === 0) { toast.info('No new staff to send (already on Crew tab).'); return; }
    setImportLoading(true);
    try {
      const res = await api.bulkImportCrew(trip.id, staffRows);
      toast.success(`Added ${res.added} to Crew tab${res.skipped?.length ? ` (${res.skipped.length} already there)` : ''}`);
      // Drop those rows from the proposed list so the import won't recreate
      // them as divers, and refresh the trip so the Crew tab updates.
      setImportPreview({
        ...importPreview,
        proposed: importPreview.proposed.filter((d) => !d.is_staff_like || d.matches_crew),
        staff_like_count: importPreview.proposed.filter((d) => d.is_staff_like && d.matches_crew).length,
      });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send to Crew');
    } finally {
      setImportLoading(false);
    }
  };

  const fetchPreview = async () => {
    if (!csvUrl.trim()) { toast.error('Paste your Google Sheet URL first'); return; }
    setImportLoading(true);
    try {
      const data = await api.csvImportPreview(trip.id, csvUrl.trim());
      setImportPreview(data);
      // Default: check rows that don't already exist AND don't look like staff.
      const checked = {};
      (data.proposed || []).forEach((d) => { checked[d.id] = !d.already_exists && !d.is_staff_like; });
      setImportChecked(checked);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not fetch CSV. Make sure it is published or set to "Anyone with the link".');
    } finally {
      setImportLoading(false);
    }
  };

  const fetchPreviewFromFile = async (file) => {
    if (!file) return;
    setImportLoading(true);
    try {
      const data = await api.csvImportPreviewFile(trip.id, file);
      setImportPreview(data);
      const checked = {};
      (data.proposed || []).forEach((d) => { checked[d.id] = !d.already_exists && !d.is_staff_like; });
      setImportChecked(checked);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read that file. Use a .csv or .xlsx export.');
    } finally {
      setImportLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    const rows = importPreview.proposed
      .filter((d) => importChecked[d.id])
      .map((d) => ({
        name: d.name,
        certification_level: d.certification_level,
        nitrox: !!d.nitrox,
        buddy_name: d.buddy_name,
        medical_notes: d.medical_notes,
        notes: d.notes,
        email: d.email,
        email_opt_in: d.email_opt_in !== false,
        prior_dives: d.prior_dives ?? null,
      }));
    if (rows.length === 0) { toast.error('Select at least one diver'); return; }
    setImportLoading(true);
    try {
      const res = await api.csvImportConfirm(trip.id, rows);
      toast.success(`Imported ${res.imported} diver${res.imported === 1 ? '' : 's'}`);
      setImportOpen(false);
      setImportPreview(null);
      setImportChecked({});
      setCsvUrl('');
      await reload();
    } catch (e) {
      toast.error('Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const submit = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      const payload = { ...form };
      payload.prior_dives = payload.prior_dives === '' || payload.prior_dives === null || payload.prior_dives === undefined ? null : Number(payload.prior_dives);
      if (editing) {
        await api.updateDiver(trip.id, editing.id, payload);
      } else {
        await api.addDiver(trip.id, payload);
      }
      toast.success(editing ? 'Diver updated' : 'Diver added');
      setOpen(false);
      setEditing(null);
      setForm(blank);
      await reload();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove diver?')) return;
    await api.deleteDiver(trip.id, id);
    await reload();
  };

  const toggleSelected = (id) => {
    setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };

  const toggleSelectedCrew = (id) => {
    setSelectedCrewIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };

  const createBuddyGroup = async () => {
    const totalPicked = selectedIds.length + selectedCrewIds.length;
    if (totalPicked < 2) { toast.error('Select 2 or more members (divers and/or staff)'); return; }
    try {
      await api.addBuddyGroup(trip.id, selectedIds, selectedCrewIds);
      toast.success('Buddy group created');
      setSelectedIds([]);
      setSelectedCrewIds([]);
      setBuddyOpen(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const removeBuddyGroup = async (gid) => {
    if (!window.confirm('Unlink this buddy group?')) return;
    await api.deleteBuddyGroup(trip.id, gid);
    await reload();
  };

  const diverMap = Object.fromEntries((trip.divers || []).map((d) => [d.id, d]));
  const crewMap = Object.fromEntries((trip.crew || []).map((c) => [c.id, c]));
  const groupedIds = new Set((trip.buddy_groups || []).flatMap((g) => g.diver_ids || []));
  const groupedCrewIds = new Set((trip.buddy_groups || []).flatMap((g) => g.crew_ids || []));
  const canBuddyUp = (trip.divers || []).length + (trip.crew || []).length >= 2;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="add-diver-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Diver
        </Button>
        <Button onClick={() => { setSelectedIds([]); setSelectedCrewIds([]); setBuddyOpen(true); }} variant="outline" className="border-[#0A2342] text-[#0A2342]" data-testid="add-buddy-group-btn" disabled={!canBuddyUp}>
          <Link2 className="w-4 h-4 mr-2" /> Buddy up
        </Button>
      </div>
      <Button
        onClick={() => { setImportOpen(true); setImportPreview(null); setCsvUrl(''); }}
        variant="outline"
        className="w-full border-[#0A2342] text-[#0A2342]"
        data-testid="import-csv-btn"
      >
        <FileSpreadsheet className="w-4 h-4 mr-2" /> Import divers (file or URL)
      </Button>

      {/* Existing buddy groups */}
      {(trip.buddy_groups || []).length > 0 && (
        <div className="bg-white border rounded-xl p-3 space-y-2">
          <div className="text-xs font-semibold text-gray-500">Buddy groups (always dive together)</div>
          {trip.buddy_groups.map((g) => {
            const diverNames = (g.diver_ids || []).map((did) => ({ id: did, label: diverMap[did]?.name || '?', isCrew: false }));
            const crewNames = (g.crew_ids || []).map((cid) => ({
              id: cid,
              label: crewMap[cid]?.name ? `${crewMap[cid].name}${crewMap[cid].role ? ` (${crewMap[cid].role})` : ''}` : '?',
              isCrew: true,
            }));
            const members = [...diverNames, ...crewNames];
            return (
              <div key={g.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`buddy-group-${g.id}`}>
                <div className="flex items-center gap-1 flex-wrap">
                  <Link2 className="w-3.5 h-3.5 text-[#FF6B35]" />
                  {members.map((m, i) => (
                    <span key={`${m.id}-${i}`} className={`font-medium ${m.isCrew ? 'text-[#FF6B35]' : 'text-[#0A2342]'}`}>
                      {m.label}{i < members.length - 1 ? ' · ' : ''}
                    </span>
                  ))}
                </div>
                <button onClick={() => removeBuddyGroup(g.id)} className="text-red-500 hover:text-red-700" aria-label="Unlink"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}

      {(trip.divers || []).length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-500">No divers yet.</div>
      ) : (
        <div className="bg-white rounded-xl border divide-y">
          {trip.divers.map((d) => (
            <div key={d.id} className="p-3 flex items-center justify-between gap-3" data-testid={`diver-${d.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-[#0A2342] truncate flex items-center gap-1">
                  {d.name}
                  {groupedIds.has(d.id) && <Link2 className="w-3.5 h-3.5 text-[#FF6B35]" />}
                  {d.skip_dive && <span className="text-xs text-amber-600">(skipping)</span>}
                </div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                  {d.certification_level && <span>{d.certification_level}</span>}
                  {d.nitrox && <span className="text-green-600">Nitrox</span>}
                  {d.email && <span className="text-[#0A2342]" title={d.email}>📧</span>}
                  {d.recap_sent_at && <span className="text-green-600" title={`Recap sent ${d.recap_sent_at}`}>✓ recap sent</span>}
                  {d.selected_destination && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-semibold" data-testid={`diver-destination-badge-${d.id}`}>
                      📍 {d.selected_destination}
                    </span>
                  )}
                  {d.buddy_name && <span>Note: {d.buddy_name}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(d); setForm({ ...blank, ...d }); setOpen(true); }} data-testid={`diver-edit-${d.id}`}><Edit2 className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(d.id)} data-testid={`diver-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/edit diver dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Diver' : 'Add Diver'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="diver-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.certification_level || ''} onValueChange={(v) => setForm({ ...form, certification_level: v })}>
                <SelectTrigger><SelectValue placeholder="Cert level" /></SelectTrigger>
                <SelectContent>{CERT_LEVELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="Logged dives (prior)"
                title="Dives logged BEFORE this trip — used to bias stronger divers onto the first launch in rough seas"
                value={form.prior_dives ?? ''}
                onChange={(e) => setForm({ ...form, prior_dives: e.target.value })}
                data-testid="diver-prior-dives-input"
              />
            </div>
            <Input placeholder="Buddy / notes" value={form.buddy_name || ''} onChange={(e) => setForm({ ...form, buddy_name: e.target.value })} />
            <Input placeholder="Medical notes" value={form.medical_notes || ''} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} />
            <Input placeholder="Other notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <Input
              type="email"
              placeholder="Email (optional — used for end-of-trip dive log recap)"
              value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="diver-email-input"
            />
            {trip.destinations && trip.destinations.length >= 2 && (
              <div>
                <label className="text-[11px] text-gray-500 block mb-1">Destination (this trip runs to multiple locations)</label>
                <Select value={form.selected_destination || ''} onValueChange={(v) => setForm({ ...form, selected_destination: v })}>
                  <SelectTrigger data-testid="diver-destination-select"><SelectValue placeholder="— Select destination —" /></SelectTrigger>
                  <SelectContent>
                    {trip.destinations.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.nitrox || false} onChange={(e) => setForm({ ...form, nitrox: e.target.checked })} /> Nitrox certified</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.skip_dive || false} onChange={(e) => setForm({ ...form, skip_dive: e.target.checked })} /> Skipping dives</label>
              <label className="flex items-center gap-2" title="When unticked, this diver is excluded from the trip recap email.">
                <input
                  type="checkbox"
                  checked={form.email_opt_in !== false}
                  onChange={(e) => setForm({ ...form, email_opt_in: e.target.checked })}
                  data-testid="diver-email-opt-in"
                /> Send dive log by email after trip
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="diver-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buddy-up dialog */}
      <Dialog open={buddyOpen} onOpenChange={setBuddyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buddy up</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 mb-2">Select 2 or more people who must always be on the same launch — couples, families, or pairing a diver with their assigned instructor / divemaster.</p>
          <div className="max-h-[55vh] overflow-y-auto border rounded-lg divide-y">
            {(trip.divers || []).length > 0 && (
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-slate-50 sticky top-0">Divers</div>
            )}
            {(trip.divers || []).map((d) => (
              <label key={d.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`buddy-pick-${d.id}`}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(d.id)}
                  onChange={() => toggleSelected(d.id)}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <div className="font-medium text-[#0A2342]">{d.name}</div>
                  <div className="text-xs text-gray-500">{d.certification_level || '—'}</div>
                </div>
                {groupedIds.has(d.id) && <span className="text-xs text-amber-600">Already in a group</span>}
              </label>
            ))}
            {(trip.crew || []).length > 0 && (
              <div className="px-3 py-2 text-xs font-semibold text-[#FF6B35] bg-slate-50 sticky top-0">Staff / Crew</div>
            )}
            {(trip.crew || []).map((c) => (
              <label key={c.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`buddy-pick-crew-${c.id}`}>
                <input
                  type="checkbox"
                  checked={selectedCrewIds.includes(c.id)}
                  onChange={() => toggleSelectedCrew(c.id)}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <div className="font-medium text-[#FF6B35]">{c.name}</div>
                  <div className="text-xs text-gray-500">{(c.role || 'staff').toString()}</div>
                </div>
                {groupedCrewIds.has(c.id) && <span className="text-xs text-amber-600">Already in a group</span>}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuddyOpen(false)}>Cancel</Button>
            <Button onClick={createBuddyGroup} disabled={selectedIds.length + selectedCrewIds.length < 2} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="buddy-group-save-btn">
              <Link2 className="w-4 h-4 mr-2" /> Link {(selectedIds.length + selectedCrewIds.length) || ''} {selectedIds.length + selectedCrewIds.length === 1 ? 'person' : 'people'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* CSV import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Import divers</DialogTitle></DialogHeader>
          {!importPreview ? (
            <div className="space-y-3">
              {/* File upload option (CSV / XLSX). Preferred path — works
                  offline with any spreadsheet, no sharing/publishing needed. */}
              <div className="border-2 border-dashed border-[#FF6B35]/40 rounded-xl p-4 bg-[#FFF6F1]">
                <div className="text-xs font-bold uppercase text-[#0A2342] mb-1">Upload a file</div>
                <p className="text-xs text-gray-600 mb-2">Drop or pick a <strong>.csv</strong> or <strong>.xlsx</strong> from your computer — no Google Sheets needed.</p>
                <input
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => fetchPreviewFromFile(e.target.files?.[0])}
                  className="block w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#FF6B35] file:text-white hover:file:bg-[#FF6B35]/90 cursor-pointer"
                  disabled={importLoading}
                  data-testid="csv-file-input"
                />
                {importLoading && (
                  <div className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Reading file…</div>
                )}
              </div>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                <div className="relative flex justify-center"><span className="bg-white px-2 text-[10px] uppercase tracking-wider text-gray-400">or paste a published URL</span></div>
              </div>

              <div className="bg-slate-50 border rounded-xl p-3 text-xs text-gray-600 space-y-1">
                <div className="font-semibold text-[#0A2342]">How to get your CSV URL:</div>
                <ol className="list-decimal pl-5 space-y-0.5">
                  <li>Open your Google Form, click <strong>Responses → View in Sheets</strong>.</li>
                  <li>In that Sheet, go to <strong>File → Share → Publish to web</strong>.</li>
                  <li>Pick "Comma-separated values (.csv)" → click <strong>Publish</strong>.</li>
                  <li>Copy the URL it shows and paste it below. (You can also paste the regular Sheet edit URL if "Anyone with the link can view" is on.)</li>
                </ol>
              </div>
              <Input
                placeholder="https://docs.google.com/spreadsheets/d/.../pub?output=csv"
                value={csvUrl}
                onChange={(e) => setCsvUrl(e.target.value)}
                data-testid="csv-url-input"
              />
              <div className="flex justify-end">
                <Button onClick={fetchPreview} disabled={importLoading || !csvUrl.trim()} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="csv-fetch-btn">
                  {importLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  Fetch &amp; preview
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{importPreview.proposed.length} response{importPreview.proposed.length === 1 ? '' : 's'} detected. Untick anyone you don't want to import.</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const all = {}; importPreview.proposed.forEach((d) => { all[d.id] = true; });
                      setImportChecked(all);
                    }}
                    className="text-[#FF6B35] hover:underline"
                  >Select all</button>
                  <button
                    onClick={() => setImportChecked({})}
                    className="text-gray-500 hover:underline"
                  >None</button>
                </div>
              </div>
              {importPreview.staff_like_count > 0 && (
                <div className="bg-[#FFF6F1] border border-[#FF6B35]/30 rounded-lg p-3 text-xs text-[#0A2342] flex items-start gap-2" data-testid="csv-staff-warning">
                  <span className="text-base">⚠️</span>
                  <div className="flex-1">
                    <strong>{importPreview.staff_like_count} row{importPreview.staff_like_count === 1 ? '' : 's'} look like staff</strong> — anyone with an Instructor/Divemaster certification or a name that matches an existing crew member is unticked by default.
                    <div className="mt-2">
                      <Button
                        size="sm"
                        onClick={sendStaffToCrew}
                        disabled={importLoading}
                        className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90 text-xs h-8"
                        data-testid="csv-send-to-crew-btn"
                      >
                        {importLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                        Send {importPreview.proposed.filter((p) => p.is_staff_like && !p.matches_crew).length} to Crew tab
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="max-h-[55vh] overflow-y-auto border rounded-xl divide-y">
                {importPreview.proposed.map((d) => (
                  <label key={d.id} className="flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`csv-row-${d.id}`}>
                    <input
                      type="checkbox"
                      checked={!!importChecked[d.id]}
                      onChange={(e) => setImportChecked({ ...importChecked, [d.id]: e.target.checked })}
                      className="w-4 h-4 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[#0A2342]">{d.name}</span>
                        {d.certification_level && <span className="text-xs bg-slate-100 text-[#0A2342] px-1.5 py-0.5 rounded">{d.certification_level}</span>}
                        {d.nitrox && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Nitrox</span>}
                        {d.already_exists && <span className="text-xs text-amber-600">already on this trip</span>}
                        {d.is_staff_like && (
                          <span className="text-[10px] font-bold uppercase bg-[#FF6B35] text-white px-1.5 py-0.5 rounded" title="Looks like staff — manage on the Crew tab instead">
                            {d.matches_crew ? 'Already crew' : 'Staff'}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        {d.email && <span>📧 {d.email}</span>}
                        {d.buddy_name && <span>Buddy: {d.buddy_name}</span>}
                        {d.medical_notes && <span>Med: {d.medical_notes}</span>}
                        {d.notes && <span>Notes: {d.notes}</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            {importPreview && (
              <Button onClick={confirmImport} disabled={importLoading} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="csv-import-confirm-btn">
                {importLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Import selected ({Object.values(importChecked).filter(Boolean).length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CrewTab = ({ trip, api, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const blank = { name: '', role: 'divemaster', preferred_launch: null, fixed: false, charged: true };
  const [form, setForm] = useState(blank);

  const submit = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    const payload = { ...form, preferred_launch: form.preferred_launch ? Number(form.preferred_launch) : null };
    try {
      let result;
      if (editing) {
        result = await api.updateCrew(trip.id, editing.id, payload);
      } else {
        result = await api.addCrew(trip.id, payload);
      }
      // Backend returns `removed_diver_count` when it auto-removed any divers
      // sharing the new crew member's name. Surface that so the operator knows.
      if (result?.removed_diver_count > 0) {
        toast.success(`Crew added · also removed ${result.removed_diver_count} matching diver entr${result.removed_diver_count === 1 ? 'y' : 'ies'}`);
      } else {
        toast.success(editing ? 'Crew updated' : 'Crew added');
      }
      setOpen(false);
      setEditing(null);
      setForm(blank);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const remove = async (id) => { if (!window.confirm('Remove crew member?')) return; await api.deleteCrew(trip.id, id); await reload(); };

  return (
    <div className="space-y-3">
      <Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="add-crew-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Crew
      </Button>
      {(trip.crew || []).length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-500">No crew yet. Every launch needs at least 1 crew member.</div>
      ) : (
        <div className="bg-white rounded-xl border divide-y">
          {trip.crew.map((c) => (
            <div key={c.id} className="p-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-[#0A2342]">{c.name}</div>
                <div className="text-xs text-gray-500 capitalize">
                  {c.role} {c.fixed && c.preferred_launch ? `• Fixed to Launch ${c.preferred_launch}` : c.preferred_launch ? `• Prefers Launch ${c.preferred_launch}` : '• Rotates'}
                  {c.charged === false && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase tracking-wide" data-testid={`crew-comped-${c.id}`}>Comped</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setForm({ ...blank, ...c }); setOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Crew' : 'Add Crew'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="divemaster">Divemaster</SelectItem>
                <SelectItem value="instructor">Instructor</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(form.preferred_launch || '')} onValueChange={(v) => setForm({ ...form, preferred_launch: v === 'none' ? null : Number(v) })}>
              <SelectTrigger><SelectValue placeholder="Preferred launch (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Rotate (no preference)</SelectItem>
                <SelectItem value="1">Launch 1</SelectItem>
                <SelectItem value="2">Launch 2</SelectItem>
                <SelectItem value="3">Launch 3</SelectItem>
                <SelectItem value="4">Launch 4</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.fixed || false} onChange={(e) => setForm({ ...form, fixed: e.target.checked })} />
              Fixed to preferred launch (no rotation)
            </label>
            <label className="flex items-center gap-2 text-sm" title="When unticked, the dive centre comps this crew member's seat — they don't add to host COGS in the P&L.">
              <input
                type="checkbox"
                checked={form.charged !== false}
                onChange={(e) => setForm({ ...form, charged: e.target.checked })}
                data-testid="crew-charged-toggle"
              />
              Host charges Just Dive for this seat
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== CYLINDERS =====================

const CylindersTab = ({ trip, api, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const blank = { label: '', size_l: 12, nitrox: false, last_serviced: '', next_service_due: '', assigned_diver_id: null, in_use: false, notes: '' };
  const [form, setForm] = useState(blank);

  const submit = async () => {
    if (!form.label) { toast.error('Label required'); return; }
    try {
      if (editing) await api.updateCylinder(trip.id, editing.id, form);
      else await api.addCylinder(trip.id, form);
      toast.success('Saved'); setOpen(false); setEditing(null); setForm(blank); await reload();
    } catch (e) { toast.error('Failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Remove cylinder?')) return; await api.deleteCylinder(trip.id, id); await reload(); };
  const diverMap = Object.fromEntries((trip.divers || []).map((d) => [d.id, d.name]));

  return (
    <div className="space-y-3">
      <Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="add-cylinder-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Cylinder
      </Button>
      {(trip.cylinders || []).length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-500">No cylinders tracked yet.</div>
      ) : (
        <div className="bg-white rounded-xl border divide-y">
          {trip.cylinders.map((c) => (
            <div key={c.id} className="p-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-[#0A2342]">{c.label} <span className="text-xs text-gray-400">{c.size_l}L {c.nitrox && '· Nitrox'}</span></div>
                <div className="text-xs text-gray-500">
                  {c.assigned_diver_id && diverMap[c.assigned_diver_id] && <span>Assigned: {diverMap[c.assigned_diver_id]}</span>}
                  {c.next_service_due && <span> · Next service: {c.next_service_due}</span>}
                  {c.in_use && <span className="text-green-600"> · In use</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setForm({ ...blank, ...c }); setOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Cylinder' : 'Add Cylinder'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Label (e.g. CYL-001)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Size (L)" value={form.size_l} onChange={(e) => setForm({ ...form, size_l: Number(e.target.value) })} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.nitrox || false} onChange={(e) => setForm({ ...form, nitrox: e.target.checked })} /> Nitrox</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Last serviced</label><Input type="date" value={form.last_serviced || ''} onChange={(e) => setForm({ ...form, last_serviced: e.target.value })} /></div>
              <div><label className="text-xs text-gray-500">Next service due</label><Input type="date" value={form.next_service_due || ''} onChange={(e) => setForm({ ...form, next_service_due: e.target.value })} /></div>
            </div>
            <Select value={form.assigned_diver_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_diver_id: v === 'none' ? null : v })}>
              <SelectTrigger><SelectValue placeholder="Assign to diver" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {(trip.divers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_use || false} onChange={(e) => setForm({ ...form, in_use: e.target.checked })} /> In use</label>
            <Input placeholder="Notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== SETTINGS =====================

const SettingsTab = ({ trip, api, reload, navigate }) => {
  const [form, setForm] = useState({
    name: trip.name,
    start_date: trip.start_date,
    end_date: trip.end_date,
    destinations: (trip.destinations || []).join('\n'),
    cost_to_client_per_dive: trip.cost_to_client_per_dive ?? '',
    cost_to_host_per_dive: trip.cost_to_host_per_dive ?? '',
    cost_to_casual_staff_per_dive: trip.cost_to_casual_staff_per_dive ?? '',
    dive_centre_name: trip.dive_centre_name || '',
    dive_centre_email: trip.dive_centre_email || '',
    site_latitude: trip.site_latitude ?? '',
    site_longitude: trip.site_longitude ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [recapSending, setRecapSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [finalising, setFinalising] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.cost_to_client_per_dive = payload.cost_to_client_per_dive === '' ? null : Number(payload.cost_to_client_per_dive);
      payload.cost_to_host_per_dive = payload.cost_to_host_per_dive === '' ? null : Number(payload.cost_to_host_per_dive);
      payload.cost_to_casual_staff_per_dive = payload.cost_to_casual_staff_per_dive === '' ? null : Number(payload.cost_to_casual_staff_per_dive);
      payload.site_latitude = payload.site_latitude === '' ? null : Number(payload.site_latitude);
      payload.site_longitude = payload.site_longitude === '' ? null : Number(payload.site_longitude);
      // Split textarea → list. Only persist when there are 2+ entries so a
      // single-destination trip stays single.
      const destsList = (payload.destinations || '')
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      payload.destinations = destsList.length >= 2 ? destsList : null;
      await api.updateTrip(trip.id, payload);
      toast.success('Saved');
      await reload();
    } catch (e) {
      toast.error('Failed');
    } finally { setSaving(false); }
  };

  const finaliseTrip = async ({ sendInvoices }) => {
    const verb = sendInvoices ? 'GENERATE & EMAIL invoices + send P&L report' : 'generate P&L report only (no invoices sent)';
    if (!window.confirm(`Finalise this trip — ${verb}. Continue?`)) return;
    setFinalising(true);
    try {
      const res = await api.finaliseTrip(trip.id, { send_invoices: sendInvoices });
      toast.success(`Done — ${res.invoices_created} invoice${res.invoices_created === 1 ? '' : 's'} created${res.invoices_emailed ? `, ${res.invoices_emailed} emailed` : ''}, P&L sent to ${res.pnl_recipients?.length || 0} recipient(s)`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalise failed');
    } finally { setFinalising(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this trip and all its data?')) return;
    await api.deleteTrip(trip.id);
    toast.success('Trip deleted');
    navigate('/planner');
  };

  // Recap email summary stats
  const divers = trip.divers || [];
  const optedIn = divers.filter((d) => d.email && d.email_opt_in !== false);
  const alreadySent = optedIn.filter((d) => d.recap_sent_at);
  const pending = optedIn.length - alreadySent.length;
  const hasAnyLogs = (trip.daily_plans || []).some((p) => (p.launches || []).some((L) => L.log));

  const sendRecap = async ({ retryAll } = {}) => {
    const word = retryAll ? 're-send to ALL opted-in divers' : `send to ${pending} pending diver${pending === 1 ? '' : 's'}`;
    if (!window.confirm(`Recap emails — ${word}?`)) return;
    setRecapSending(true);
    try {
      const res = await api.sendRecapEmails(trip.id, { only_unsent: !retryAll });
      toast.success(`Sent ${res.sent} · Skipped ${res.skipped}${res.failed ? ` · Failed ${res.failed}` : ''}`);
      await reload();
    } catch (e) {
      toast.error('Send failed');
    } finally {
      setRecapSending(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) { toast.error('Enter a valid test email'); return; }
    setRecapSending(true);
    try {
      await api.sendRecapEmails(trip.id, { test_to: testEmail.trim() });
      toast.success(`Test email sent to ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setRecapSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <Input placeholder="Trip name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>

        {/* Cost centre — set this at the start of the trip so finalise can
            compute per-diver invoices + a profit/loss report. */}
        <div className="border-t pt-3">
          <div className="text-xs font-bold uppercase text-[#0A2342] mb-2">Cost centre</div>
          <p className="text-[11px] text-gray-500 mb-2">Set per-dive pricing. Charters and centres vary per trip — these drive the auto-invoice and P&amp;L report at finalise.</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">Cost to client (per dive, R)</label>
              <Input type="number" min="0" step="0.01" placeholder="650.00" value={form.cost_to_client_per_dive} onChange={(e) => setForm({ ...form, cost_to_client_per_dive: e.target.value })} data-testid="trip-cost-to-client" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Cost to host (per dive, R)</label>
              <Input type="number" min="0" step="0.01" placeholder="450.00" value={form.cost_to_host_per_dive} onChange={(e) => setForm({ ...form, cost_to_host_per_dive: e.target.value })} data-testid="trip-cost-to-host" />
            </div>
          </div>
          <div className="mt-2">
            <label className="text-[11px] text-gray-500" title="When a crew member dives casually (sits in the DIVERS list, not CREW), they pay this rate. Leave blank to bill the full client rate.">
              Casual staff rate (per dive, R) <span className="text-gray-400">— optional</span>
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Leave blank → full client rate"
              value={form.cost_to_casual_staff_per_dive}
              onChange={(e) => setForm({ ...form, cost_to_casual_staff_per_dive: e.target.value })}
              data-testid="trip-cost-to-casual-staff"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input placeholder="Dive centre / charter name" value={form.dive_centre_name} onChange={(e) => setForm({ ...form, dive_centre_name: e.target.value })} data-testid="trip-dive-centre-name" />
            <Input type="email" placeholder="Dive centre email (for P&L)" value={form.dive_centre_email} onChange={(e) => setForm({ ...form, dive_centre_email: e.target.value })} data-testid="trip-dive-centre-email" />
          </div>
        </div>

        {/* Multi-destination — when a trip runs to two locations on the
            same dates (e.g. Sodwana OR Ponta), list them here so the diver
            dialog can tag each diver with the destination they picked at
            booking. */}
        <div className="border-t pt-3">
          <div className="text-xs font-bold uppercase text-[#0A2342] mb-2">Multi-destination (optional)</div>
          <p className="text-[11px] text-gray-500 mb-2">
            One destination per line. When 2+ entries are set, the diver
            form shows a Destination dropdown so you can see who&apos;s going
            where on the day. Leave blank for single-destination trips.
          </p>
          <textarea
            className="w-full border rounded p-2 text-sm bg-white"
            rows={3}
            placeholder={"Sodwana Bay\nPonta do Ouro"}
            value={form.destinations}
            onChange={(e) => setForm({ ...form, destinations: e.target.value })}
            data-testid="trip-destinations-input"
          />
        </div>

        {/* Dive site coordinates — drives marine forecast (Open-Meteo) on
            plan generation. Leave blank to skip weather. */}
        <div className="border-t pt-3">
          <div className="text-xs font-bold uppercase text-[#0A2342] mb-2">Dive site coordinates</div>
          <p className="text-[11px] text-gray-500 mb-2">When set, each generated plan pulls a sea-state forecast (waves, swell, wind) for the dive day. Use decimal degrees — e.g. Aliwal Shoal: <span className="font-mono">-30.265, 30.835</span></p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">Latitude</label>
              <Input type="number" step="0.0001" placeholder="-30.265" value={form.site_latitude} onChange={(e) => setForm({ ...form, site_latitude: e.target.value })} data-testid="trip-site-latitude" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Longitude</label>
              <Input type="number" step="0.0001" placeholder="30.835" value={form.site_longitude} onChange={(e) => setForm({ ...form, site_longitude: e.target.value })} data-testid="trip-site-longitude" />
            </div>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90 w-full" data-testid="trip-settings-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
        </Button>
      </div>

      {/* End-of-trip finalise — invoices + P&L */}
      <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="finalise-card">
        <div>
          <div className="font-semibold text-[#0A2342]">Finalise trip</div>
          <p className="text-xs text-gray-500">Counts every diver&apos;s actual dives × cost-to-client and creates one invoice each via the central Invoices system. Emails admin + dive centre a P&amp;L summary.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => finaliseTrip({ sendInvoices: true })} disabled={finalising} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 flex-1 min-w-[200px]" data-testid="finalise-with-invoices-btn">
            {finalising ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Generate &amp; email invoices
          </Button>
          <Button onClick={() => finaliseTrip({ sendInvoices: false })} disabled={finalising} variant="outline" className="flex-1 min-w-[200px]" data-testid="finalise-no-invoices-btn">
            {finalising ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} P&amp;L only (no invoice emails)
          </Button>
        </div>
        <div className="border-t pt-3">
          <div className="text-xs font-bold uppercase text-[#0A2342] mb-1">Dive recon</div>
          <p className="text-[11px] text-gray-500 mb-2">A per-diver breakdown of every dive on this trip — site, depth, time, conditions.</p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <Button variant="outline" className="text-xs" onClick={() => api.downloadDiveRecon(trip.id, 'pdf')} data-testid="dive-recon-pdf">PDF</Button>
            <Button variant="outline" className="text-xs" onClick={() => api.downloadDiveRecon(trip.id, 'xlsx')} data-testid="dive-recon-xlsx">Excel</Button>
          </div>
          <p className="text-[11px] text-gray-500 mb-1"><strong>Cost review</strong> — same recon plus per-diver revenue/cost/profit and a P&amp;L summary header. Use this to sanity-check the numbers before clicking Finalise.</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="text-xs border-[#FF6B35] text-[#FF6B35] hover:bg-[#FFF6F1]" onClick={() => api.downloadDiveRecon(trip.id, 'pdf', true)} data-testid="dive-recon-pdf-costs">PDF (with costs)</Button>
            <Button variant="outline" className="text-xs border-[#FF6B35] text-[#FF6B35] hover:bg-[#FFF6F1]" onClick={() => api.downloadDiveRecon(trip.id, 'xlsx', true)} data-testid="dive-recon-xlsx-costs">Excel (with costs)</Button>
          </div>
        </div>
      </div>

      {/* End-of-trip dive log recap emails */}
      <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="recap-card">
        <div>
          <div className="font-semibold text-[#0A2342] flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#FF6B35]" /> End-of-trip dive log emails
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Send each diver their own logged-dive recap email — branded with Just Dive colors and a "Book your next trip" CTA. Only divers with an email AND the opt-in tick get one.
          </p>
        </div>

        <div className="text-xs text-[#0A2342] grid grid-cols-3 gap-2">
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <div className="text-base font-bold">{optedIn.length}</div>
            <div className="text-[10px] text-gray-500">opted in</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <div className="text-base font-bold">{alreadySent.length}</div>
            <div className="text-[10px] text-gray-500">already sent</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <div className="text-base font-bold text-[#FF6B35]">{pending}</div>
            <div className="text-[10px] text-gray-500">pending</div>
          </div>
        </div>

        {!hasAnyLogs && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            ⚠ No instructor dive logs recorded yet. Recap emails are only sent for divers with at least one logged dive.
          </div>
        )}

        <div className="space-y-2">
          <Input
            type="email"
            placeholder="Send a test email to yourself first (recommended)"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            data-testid="recap-test-email-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={sendTest}
              disabled={recapSending || !testEmail || !hasAnyLogs}
              variant="outline"
              className="border-[#0A2342] text-[#0A2342]"
              data-testid="recap-test-btn"
            >
              {recapSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send test
            </Button>
            <Button
              onClick={() => sendRecap({ retryAll: false })}
              disabled={recapSending || pending === 0 || !hasAnyLogs}
              className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
              data-testid="recap-send-pending-btn"
            >
              {recapSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send to {pending} pending
            </Button>
          </div>
          {alreadySent.length > 0 && (
            <Button
              onClick={() => sendRecap({ retryAll: true })}
              disabled={recapSending || !hasAnyLogs}
              variant="ghost"
              className="w-full text-xs text-gray-500"
              data-testid="recap-resend-all-btn"
            >
              Re-send to all {optedIn.length} opted-in divers (including already sent)
            </Button>
          )}
        </div>
      </div>

      <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>
        <Trash2 className="w-4 h-4 mr-2" /> Delete Trip
      </Button>
    </div>
  );
};

export default TripDetail;
